import React, { useState, useEffect } from 'react';

export interface CdrPaginationControlsProps {
  cdrsPage: number;
  cdrsTotal: number;
  cdrsGroupsCount: number;
  loadCdrs: (page?: number) => void;
  isLight: boolean;
  isBottom?: boolean;
  searchArchive?: boolean;
}

export function CdrPaginationControls({
  cdrsPage,
  cdrsTotal,
  cdrsGroupsCount,
  loadCdrs,
  isLight,
  isBottom = false,
  searchArchive = false,
}: CdrPaginationControlsProps) {
  const [pageInput, setPageInput] = useState('1');

  useEffect(() => {
    setPageInput(String(cdrsPage + 1));
  }, [cdrsPage]);

  const textColor = isLight ? '#09090b' : 'white';
  const mutedColor = isLight ? '#6b7280' : '#a1a1aa';
  const borderColor = isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.05)';
  const isNextDisabled = cdrsTotal > 0 ? cdrsPage >= Math.max(0, Math.ceil(cdrsTotal / 25) - 1) : cdrsGroupsCount < 25;

  const handlePageCommit = () => {
    let p = parseInt(pageInput, 10);
    if (isNaN(p) || p < 1) p = 1;
    if (cdrsTotal > 0) {
      const maxP = Math.max(1, Math.ceil(cdrsTotal / 25));
      if (p > maxP) p = maxP;
    }
    setPageInput(String(p));
    if (p - 1 !== cdrsPage) {
      loadCdrs(p - 1);
      window.scrollTo(0, 0);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
      {!isBottom && cdrsTotal > 0 && (
        <div style={{ fontSize: '0.75rem', color: mutedColor, marginBottom: '-0.25rem' }}>
          Page {cdrsPage + 1} of {Math.max(1, Math.ceil(cdrsTotal / 25)).toLocaleString()} ({cdrsTotal.toLocaleString()} calls)
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: mutedColor, fontSize: '0.9rem' }}>
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <button
            onClick={() => { loadCdrs(0); window.scrollTo(0, 0); }}
            disabled={cdrsPage === 0}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem 0.375rem', background: isLight ? 'white' : 'rgba(255,255,255,0.1)', color: textColor, border: `1px solid ${borderColor}`, borderRadius: '4px', cursor: cdrsPage === 0 ? 'not-allowed' : 'pointer', opacity: cdrsPage === 0 ? 0.5 : 1 }}
            title="First Page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 17 6 12 11 7"></polyline><polyline points="18 17 13 12 18 7"></polyline></svg>
          </button>
          <button
            onClick={() => { loadCdrs(cdrsPage > 0 ? cdrsPage - 1 : 0); window.scrollTo(0, 0); }}
            disabled={cdrsPage === 0}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem 0.75rem', background: isLight ? 'white' : 'rgba(255,255,255,0.1)', color: textColor, border: `1px solid ${borderColor}`, borderRadius: '4px', cursor: cdrsPage === 0 ? 'not-allowed' : 'pointer', opacity: cdrsPage === 0 ? 0.5 : 1 }}
            title="Previous Page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
          </button>
        </div>

        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="text"
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handlePageCommit();
              }
            }}
            onBlur={handlePageCommit}
            style={{ width: '40px', textAlign: 'center', background: 'transparent', color: textColor, border: `1px solid ${borderColor}`, borderRadius: '4px', padding: '0.25rem' }}
          />
        </span>

        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <button
            onClick={() => { loadCdrs(cdrsPage + 1); window.scrollTo(0, 0); }}
            disabled={isNextDisabled}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem 0.75rem', background: isLight ? 'white' : 'rgba(255,255,255,0.1)', color: textColor, border: `1px solid ${borderColor}`, borderRadius: '4px', cursor: isNextDisabled ? 'not-allowed' : 'pointer', opacity: isNextDisabled ? 0.5 : 1 }}
            title="Next Page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </button>
          {cdrsTotal > 0 && (
            <button
              onClick={() => { loadCdrs(Math.max(0, Math.ceil(cdrsTotal / 25) - 1)); window.scrollTo(0, 0); }}
              disabled={isNextDisabled}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem 0.375rem', background: isLight ? 'white' : 'rgba(255,255,255,0.1)', color: textColor, border: `1px solid ${borderColor}`, borderRadius: '4px', cursor: isNextDisabled ? 'not-allowed' : 'pointer', opacity: isNextDisabled ? 0.5 : 1 }}
              title="Last Page"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="13 17 18 12 13 7"></polyline><polyline points="6 17 11 12 6 7"></polyline></svg>
            </button>
          )}
        </div>
      </div>
      {isBottom && cdrsTotal > 0 && (
        <div style={{ fontSize: '0.75rem', color: mutedColor, marginTop: '-0.25rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span>Page {cdrsPage + 1} of {Math.max(1, Math.ceil(cdrsTotal / 25)).toLocaleString()} ({cdrsTotal.toLocaleString()} calls)</span>
          {searchArchive && (
            <span style={{ color: isLight ? '#0284c7' : '#38bdf8', fontWeight: 600 }}>• Cold Archive</span>
          )}
        </div>
      )}
    </div>
  );
}
